import { useState } from 'react'
import { Plus } from 'lucide-react'
import { withCdnImageWidth } from '@/lib/cdnImage'
import { potencySuffix } from '@/lib/potency'
import { ProductIcon, getCategoryIcon } from '@/lib/icons'
import { getPriceDisplay } from '@/lib/pricing'
import { getProductTypeName, typeBadgeClasses } from '@/lib/productType'
import { formatCurrency } from '@/lib/utils'
import type { Product } from '@/types'

export interface ProductCardProps {
  product: Product
  onAddToCart: (product: Product) => void
  onClick: (product: Product) => void
  isFocused?: boolean
  cartQuantity?: number
}

// Format THC/CBD display - handles both number and string ranges (max 2 decimals)
function formatPercent(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '0'
  if (typeof value === 'number') {
    if (value % 1 === 0) return `${value}`
    // Max 2 decimal places, remove trailing zeros
    return parseFloat(value.toFixed(2)).toString()
  }
  // If string, try to parse and format
  const num = parseFloat(value)
  if (!isNaN(num)) {
    if (num % 1 === 0) return `${num}`
    return parseFloat(num.toFixed(2)).toString()
  }
  return value
}

// Check if CBD value is significant enough to display
function shouldShowCbd(cbd: string | number | null | undefined): boolean {
  if (cbd === null || cbd === undefined) return false
  const num = typeof cbd === 'number' ? cbd : parseFloat(cbd)
  if (!Number.isFinite(num)) return false
  return num > 0.5
}

// Show the weight whenever the product has a positive `mj_grams`, regardless of
// `is_cannabis`. Prefer the human-readable `product_unit` (e.g. "3x0.5g") when
// available; otherwise fall back to `mj_grams` rounded to a whole number + "g".
function getDisplayWeight(product: Product): string {
  const raw = product.mj_grams
  const grams = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''))
  if (!Number.isFinite(grams) || grams <= 0) return ''
  if (product.product_unit) return product.product_unit
  return `${Math.round(grams)}g`
}

function toNum(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  return Number.isFinite(n) ? n : null
}

function getOnHand(product: Product): number | null {
  const n = toNum(product.on_hand)
  if (n === null) return null
  return Math.max(0, Math.floor(n))
}

/** Thumbnail size for this card’s 40×40 image slot (CDN `width` param). */
const PRODUCT_CARD_IMAGE_WIDTH = 150

// Get display fields from API product. THC/CBD are cannabis-only; weight is
// shown whenever `mj_grams` is positive (cannabis or not).
function getProductFields(product: Product) {
  const isCannabis = product.is_cannabis === true
  const category =
    (typeof product.category === 'string' && product.category) ||
    (typeof product.category_name === 'string' && product.category_name) ||
    ''
  return {
    name: product.display_name || product.description || product.name || 'Unknown Product',
    brand: product.brand_name || product.vendor_name || product.brand || '',
    category,
    typeName: getProductTypeName(product),
    thc: isCannabis ? (product.test_thc ?? product.thc_percent ?? null) : null,
    unit: isCannabis ? (product.test_units ?? null) : null,
    cbd: isCannabis ? (product.test_cbd ?? product.cbd_percent ?? null) : null,
    size: getDisplayWeight(product),
    icon: getCategoryIcon(category),
    image: product.image_url || product.thumbnail_url || product.image || null,
  }
}

// Grid view - Compact card
export function ProductCard({ product, onAddToCart, onClick, isFocused, cartQuantity }: ProductCardProps) {
  const p = getProductFields(product)
  const [imgError, setImgError] = useState(false)
  const { regular, salePill, memberPill } = getPriceDisplay(product)
  const onHand = getOnHand(product)

  return (
    <div
      className={`bg-white rounded-2xl border p-3 hover:shadow-lg hover:shadow-[#44BC78]/5 hover:border-green-400 transition-all cursor-pointer group h-full flex flex-col ${
        isFocused ? 'border-green-400 shadow-lg shadow-[#44BC78]/10 ring-2 ring-green-300' : 'border-gray-200'
      }`}
      onClick={() => onClick(product)}
    >
      {/* Top: Image/Icon + Name/Brand */}
      <div className="flex items-start gap-2.5 mb-2">
        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600 flex-shrink-0 overflow-hidden">
          {p.image && !imgError ? (
            <img
              src={withCdnImageWidth(p.image, PRODUCT_CARD_IMAGE_WIDTH)}
              alt={p.name}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <ProductIcon name={p.icon} size={20} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">
            {p.name}
          </h3>
          {p.brand && <p className="text-xs text-gray-500">{p.brand}</p>}
        </div>
      </div>

      {/* Category + Format */}
      {p.category && (
        <div className="text-xs text-gray-400 mb-1">
          {p.category}
          {product.format && ` · ${product.format}`}
        </div>
      )}

      {/* Badges row: Type, THC, CBD, Size, IOH, Tags */}
      <div className="flex items-center gap-1.5 text-xs mb-2 flex-wrap">
        {p.typeName && (
          <span className={`px-1.5 py-0.5 rounded font-medium ${typeBadgeClasses(p.typeName)}`}>
            {p.typeName}
          </span>
        )}
        {p.thc !== null && (
          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
            THC {formatPercent(p.thc)}{potencySuffix(p.unit)}
          </span>
        )}
        {shouldShowCbd(p.cbd) && (
          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
            CBD {formatPercent(p.cbd)}{potencySuffix(p.unit)}
          </span>
        )}
        {p.size && (
          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
            {p.size}
          </span>
        )}
        {onHand !== null && (
          <span
            className={`px-1.5 py-0.5 rounded font-medium ${
              onHand <= 1 ? 'bg-red-50 text-red-600 font-semibold' : 'bg-gray-100 text-gray-600'
            }`}
          >
            IOH: {onHand}
          </span>
        )}
        {product.tags?.map((tag) => (
          <span
            key={tag}
            className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Spacer to push bottom to end */}
      <div className="flex-1"></div>

      {/* Bottom: Price + Add */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-auto">
        <div className="flex items-baseline gap-1.5">
          <span className="text-base font-bold text-gray-900">
            {formatCurrency(regular)}
          </span>
          {salePill !== null && (
            <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full font-medium">
              {formatCurrency(salePill)}
            </span>
          )}
          {memberPill !== null && (
            <span className="text-[10px] bg-black text-white px-2 py-0.5 rounded-full font-medium">
              {formatCurrency(memberPill)}
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onAddToCart(product)
          }}
          className={`p-2.5 md:p-1.5 rounded-lg cursor-pointer ${
            cartQuantity
              ? 'bg-green-100 border border-green-300 hover:bg-green-200 text-green-700'
              : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
          }`}
        >
          {cartQuantity ? (
            <span className="text-xs font-bold w-4 h-4 flex items-center justify-center">{cartQuantity}</span>
          ) : (
            <Plus size={16} />
          )}
        </button>
      </div>
    </div>
  )
}

// Row/List view - Same elements, horizontal layout
export function ProductRowCard({ product, onAddToCart, onClick, isFocused, cartQuantity }: ProductCardProps) {
  const p = getProductFields(product)
  const [imgError, setImgError] = useState(false)
  const { regular, salePill, memberPill } = getPriceDisplay(product)
  const onHand = getOnHand(product)

  return (
    <div
      className={`bg-white rounded-2xl border p-3 hover:shadow-lg hover:shadow-[#44BC78]/5 hover:border-green-400 transition-all cursor-pointer group flex items-center gap-3 ${
        isFocused ? 'border-green-400 shadow-lg shadow-[#44BC78]/10 ring-2 ring-green-300' : 'border-gray-200'
      }`}
      onClick={() => onClick(product)}
    >
      {/* Image/Icon */}
      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600 flex-shrink-0 overflow-hidden">
        {p.image && !imgError ? (
          <img
            src={withCdnImageWidth(p.image, PRODUCT_CARD_IMAGE_WIDTH)}
            alt={p.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <ProductIcon name={p.icon} size={20} />
        )}
      </div>

      {/* Name/Brand/Category */}
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-gray-900 text-base leading-tight">{p.name}</h3>
        <p className="text-sm text-gray-500">
          {p.brand}
          {p.brand && p.category && <span className="text-gray-300 mx-1">·</span>}
          {p.category}
          {product.format && (
            <>
              <span className="text-gray-300 mx-1">·</span>
              {product.format}
            </>
          )}
        </p>
      </div>

      {/* Badges: Type, THC, CBD, Size, IOH, Tags */}
      <div className="flex items-center gap-1.5 text-xs flex-shrink-0">
        {p.typeName && (
          <span className={`px-1.5 py-0.5 rounded font-medium ${typeBadgeClasses(p.typeName)}`}>
            {p.typeName}
          </span>
        )}
        {p.thc !== null && (
          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
            THC {formatPercent(p.thc)}{potencySuffix(p.unit)}
          </span>
        )}
        {shouldShowCbd(p.cbd) && (
          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
            CBD {formatPercent(p.cbd)}{potencySuffix(p.unit)}
          </span>
        )}
        {p.size && (
          <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
            {p.size}
          </span>
        )}
        {onHand !== null && (
          <span
            className={`px-1.5 py-0.5 rounded font-medium ${
              onHand <= 1 ? 'bg-red-50 text-red-600 font-semibold' : 'bg-gray-100 text-gray-600'
            }`}
          >
            IOH: {onHand}
          </span>
        )}
        {product.tags?.map((tag) => (
          <span
            key={tag}
            className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Price + Add (right side) */}
      <div className="flex items-center gap-3 ml-auto flex-shrink-0">
        <div className="flex items-baseline gap-1.5">
          <span className="text-base font-bold text-gray-900">
            {formatCurrency(regular)}
          </span>
          {salePill !== null && (
            <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full font-medium">
              {formatCurrency(salePill)}
            </span>
          )}
          {memberPill !== null && (
            <span className="text-[10px] bg-black text-white px-2 py-0.5 rounded-full font-medium">
              {formatCurrency(memberPill)}
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onAddToCart(product)
          }}
          className={`p-2.5 md:p-1.5 rounded-lg cursor-pointer ${
            cartQuantity
              ? 'bg-green-100 border border-green-300 hover:bg-green-200 text-green-700'
              : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
          }`}
        >
          {cartQuantity ? (
            <span className="text-xs font-bold w-4 h-4 flex items-center justify-center">{cartQuantity}</span>
          ) : (
            <Plus size={16} />
          )}
        </button>
      </div>
    </div>
  )
}
